/**
 * AppointmentManager — simple list-with-create-form for now.
 * Backed by lead_tasks where kind='appointment'.
 */
import { useEffect, useState } from "react";
import { Calendar, Plus, Loader2, MapPin } from "lucide-react";
import { api } from "../lib/api";

export default function AppointmentManager({ onOpenLead }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ lead_id: "", when: "", kind_label: "Test Drive", location: "", notes: "", notify_customer: true });
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/dealer/appointments"),
        api.get("/dealer/leads?contacted=true"),
      ]);
      setItems(a.data.items || []);
      setLeads(b.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lead_id || !form.when) return;
    setBusy(true);
    try {
      const whenIso = new Date(form.when).toISOString();
      await api.post("/dealer/appointments", { ...form, when: whenIso });
      setShowForm(false);
      setForm({ lead_id: "", when: "", kind_label: "Test Drive", location: "", notes: "", notify_customer: true });
      await load();
    } catch (ex) {
      window.alert(ex?.response?.data?.detail || "Couldn't create appointment");
    } finally { setBusy(false); }
  };

  const grouped = items.reduce((acc, t) => {
    const key = (t.due_at || "").slice(0, 10);
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-3" data-testid="appointments">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/70">Appointments</div>
          <div className="text-white font-black text-2xl">{items.length} scheduled</div>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="ssc-btn ssc-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="appt-new">
          <Plus size={14} /> New appointment
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-2xl border border-white/15 bg-white/5 p-4 space-y-2" data-testid="appt-form">
          <Field label="Lead">
            <select className="ssc-dealer-input w-full" value={form.lead_id} onChange={(e) => setForm({ ...form, lead_id: e.target.value })} required data-testid="appt-lead">
              <option value="">Pick a lead…</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.name || "Anonymous"} {l.phone ? `· ${l.phone}` : ""}</option>)}
            </select>
          </Field>
          <div className="grid sm:grid-cols-2 gap-2">
            <Field label="When">
              <input type="datetime-local" className="ssc-dealer-input w-full" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} required data-testid="appt-when" />
            </Field>
            <Field label="Type">
              <select className="ssc-dealer-input w-full" value={form.kind_label} onChange={(e) => setForm({ ...form, kind_label: e.target.value })} data-testid="appt-type">
                <option>Test Drive</option><option>Delivery</option>
                <option>Trade Appraisal</option><option>Paperwork</option><option>Follow-up</option>
              </select>
            </Field>
          </div>
          <Field label="Location">
            <input className="ssc-dealer-input w-full" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Dealership / their location" data-testid="appt-location" />
          </Field>
          <Field label="Notes">
            <textarea className="ssc-dealer-input w-full" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="appt-notes" />
          </Field>
          <label className="text-xs text-white/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={form.notify_customer} onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })} data-testid="appt-notify" />
            Email confirmation to customer
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary text-sm" data-testid="appt-submit">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Create"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="ssc-btn ssc-btn-secondary text-sm">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <div className="text-white/60 text-sm">Loading…</div>
       : Object.keys(grouped).length === 0 ? <div className="text-white/60 text-sm">No appointments scheduled.</div>
       : Object.keys(grouped).sort().map((day) => (
        <section key={day} className="rounded-2xl border border-white/15 bg-white/5 overflow-hidden" data-testid={`appt-day-${day}`}>
          <div className="px-4 py-2 text-white font-black text-sm uppercase tracking-wider bg-white/5">
            {new Date(day + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
          </div>
          <ul className="bg-white divide-y divide-[var(--ssc-line)]">
            {grouped[day].map((t) => (
              <li key={t.id} className="px-4 py-2 flex items-center gap-3 hover:bg-[var(--ssc-paper)] cursor-pointer"
                  onClick={() => t.lead?.id && onOpenLead?.(t.lead.id)} data-testid={`appt-item-${t.id}`}>
                <Calendar size={15} className="text-[var(--ssc-blue)]" />
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[var(--ssc-navy)] text-sm">{t.lead?.name || "Lead"} · {t.kind_label || "Appointment"}</div>
                  <div className="text-xs text-[#6b7c93]">
                    {new Date(t.due_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    {t.location && <span><MapPin size={10} className="inline mx-1" />{t.location}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="block text-[10px] font-extrabold uppercase tracking-widest text-white/70 mb-1">{label}</label>{children}</div>
);
